import React, { useState, useEffect, useRef, useCallback } from 'react';
import { bookingApi } from '../../../utils/bookingApi';
import { formatDate } from '../../../utils/dates';
import { Chevron, InlineError } from '../../../components/ui';
import './AdminBgNumbers.css';

// What happened to each row of a British Gymnastics lapsed-members report.
export const OUTCOMES = {
  expire: { label: 'Marked expired', preview: 'Will be marked expired', dismissed: 'Would have been marked expired (dismissed, not changed)', show: true },
  renew: { label: 'Renewed: marked verified', preview: 'Will be marked verified', show: true },
  no_membership: { label: 'Verified in the app but no current BG membership in the report: please check', show: true },
  dob_mismatch: { label: "BG number matched but date of birth didn't: please check", show: true },
  no_dob: { label: 'BG number matched but no date of birth in the app: please check', show: true },
  renewed_since: { label: 'Changed or verified since the report, so left alone', show: true },
  error: { label: "Couldn't be updated: please check", show: true },
  already_expired: { label: 'Already expired' },
  invalid: { label: 'Number already marked invalid' },
  archived: { label: 'Archived gymnast' },
  not_found: { label: 'Not in the app with that BG number' },
  current: { label: 'Membership not yet expired' },
  member_current: { label: 'Current member, already verified' },
  unreadable: { label: 'No BG number on the row' },
};

const fmtDate = (iso) => (iso ? formatDate(iso) : '');

function Outcomes({ rows, preview, dismissed, onUndo, undoing }) {
  const groups = Object.entries(OUTCOMES)
    .map(([key, meta]) => ({ key, meta, rows: rows.filter(r => r.outcome === key) }))
    .filter(g => g.rows.length > 0);
  return groups.map(({ key, meta, rows: group }) => (
    <div key={key} className="bgl-group">
      <p className={meta.show ? 'bgl-group__head bgl-group__head--strong' : 'bgl-group__head'}>
        {(dismissed && meta.dismissed) || (preview && meta.preview) || meta.label}: {group.length}
      </p>
      {meta.show && (
        <ul className="bgl-list">
          {group.map(r => (
            <li key={`${r.number}-${r.gymnastId}`}>
              {r.gymnastName} <span className="bk-muted">(BG {r.number}, {r.membership !== undefined ? (r.membership || 'no current membership') : `expired ${r.expiry}`})</span>
              {onUndo && (key === 'renew' || (key === 'expire' && r.previous)) && (r.undoneAt
                ? <span className="bk-muted"> (undone)</span>
                : (
                  <button
                    type="button"
                    className="bk-btn bk-btn--sm bgl-undo"
                    disabled={undoing === r.gymnastId}
                    aria-label={`Undo ${key === 'renew' ? 'verification' : 'expiry'} for ${r.gymnastName}`}
                    onClick={() => onUndo(r.gymnastId)}
                  >
                    Undo
                  </button>
                ))}
            </li>
          ))}
        </ul>
      )}
    </div>
  ));
}

// Upload a BG lapsed-members report (preview first, then confirm), and see
// recent reports, including ones read from the club mailbox, with Undo.
export default function BgLapseReports({ onChanged }) {
  const [reports, setReports] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [undoing, setUndoing] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [mailbox, setMailbox] = useState(null);
  const [deciding, setDeciding] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(() => bookingApi.getBgLapseReports()
    .then(r => { setReports(r.data); setLoadError(false); })
    .catch((err) => { console.error('Could not load BG reports', err); setLoadError(true); }), []);
  useEffect(() => { load(); }, [load]);

  const reset = () => { setFile(null); setPreview(null); if (inputRef.current) inputRef.current.value = ''; };

  const choose = async (f) => {
    setError(null);
    setPreview(null);
    setFile(f);
    if (!f) return;
    setBusy(true);
    try {
      const res = await bookingApi.uploadBgLapseReport(f, true);
      setPreview(res.data.rows);
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't read that file.");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await bookingApi.uploadBgLapseReport(file, false);
      reset();
      setOpen(res.data.import.id);
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't process that file.");
    } finally {
      setBusy(false);
    }
  };

  const undo = async (importId, gymnastId) => {
    setUndoing(gymnastId);
    setError(null);
    try {
      await bookingApi.undoBgLapse(importId, gymnastId);
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't undo. Try again.");
    } finally {
      setUndoing(null);
    }
  };

  const decide = async (importId, action) => {
    setDeciding(importId);
    setError(null);
    try {
      const res = await (action === 'confirm' ? bookingApi.confirmBgLapse(importId) : bookingApi.dismissBgLapse(importId));
      if (action === 'confirm') setOpen(res.data.id);
      await load();
      if (action === 'confirm') onChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't do that. Try again.");
      await load();
    } finally {
      setDeciding(null);
    }
  };

  const checkMailbox = async () => {
    setBusy(true);
    setError(null);
    setMailbox(null);
    try {
      const res = await bookingApi.checkBgLapseMailbox();
      setMailbox(res.data);
      await load();
      if (res.data.results?.some(r => r.expired > 0)) onChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't check the mailbox. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const pending = reports.filter(r => r.status === 'PENDING');
  const toExpire = preview ? preview.filter(r => r.outcome === 'expire').length : 0;
  const toRenew = preview ? preview.filter(r => r.outcome === 'renew').length : 0;
  const reportSummary = (rep) => {
    if (rep.status === 'PENDING') return 'waiting for you to confirm';
    if (rep.status === 'DISMISSED') return 'dismissed, nobody changed';
    if (rep.status === 'APPLYING') return 'confirming stopped part-way: please check';
    const renewed = (rep.rows || []).filter(r => r.outcome === 'renew').length;
    return renewed > 0 || (rep.rows || []).some(r => r.membership !== undefined)
      ? `${renewed} marked verified` : `${rep.expiredCount} marked expired`;
  };

  return (
    <section className="bk-card bgl" aria-labelledby="bg-lapse-title">
      <h2 id="bg-lapse-title" className="bk-section-title">BG lapsed members reports</h2>
      <p className="bk-small bgl-intro">
        Reports emailed to the club are read each morning and wait here until you confirm them. You can also upload one here.
        Gymnasts are matched by BG number and date of birth. A lapsed members report marks matched gymnasts expired
        (14 days' grace, parents emailed, memberships set to cancel). An Organisation Members Report marks anyone
        with a current membership as verified, which also stops their club membership from cancelling.
      </p>
      {loadError && <InlineError onRetry={load}>Couldn't load recent reports.</InlineError>}
      <label className="bk-label">
        Upload a report
        <input ref={inputRef} type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" disabled={busy} onChange={e => choose(e.target.files[0] || null)} />
      </label>
      <div className="bk-actions">
        <button type="button" className="bk-btn" disabled={busy} onClick={checkMailbox}>
          Check mailbox now
        </button>
      </div>
      {busy && !preview && <p className="bk-small">Working…</p>}
      {mailbox && (
        <div role="status" className="bgl-mailbox">
          {mailbox.error && <p className="bk-error">{mailbox.error}</p>}
          {mailbox.skipped && <p>{mailbox.skipped}</p>}
          {mailbox.results && mailbox.results.length === 0 && (
            <p>No BG reports from the listed addresses in the last 14 days.</p>
          )}
          {mailbox.results?.length > 0 && (
            <ul className="bgl-list">
              {mailbox.results.map((r, i) => (
                <li key={i}>
                  {r.fileName || 'Report'} <span className="bk-muted">from {r.sender}{r.date ? `, ${fmtDate(r.date)}` : ''}</span>
                  {': '}
                  {r.skipped || (r.duplicate ? 'already processed' : r.waiting ? 'waiting for you to confirm, below' : `processed, ${r.expired} marked expired`)}
                </li>
              ))}
            </ul>
          )}
          {mailbox.otherEmails > 0 && (
            <p className="bk-muted">
              {mailbox.otherEmails} other email{mailbox.otherEmails === 1 ? '' : 's'} from these addresses had no report and {mailbox.otherEmails === 1 ? 'was' : 'were'} left alone.
            </p>
          )}
        </div>
      )}
      {error && <p className="bk-form-error" role="alert">{error}</p>}

      {preview && (
        <div className="bgl-preview">
          <Outcomes rows={preview} preview />
          <div className="bk-actions">
            <button type="button" className="bk-btn bk-btn--primary" disabled={busy || toExpire + toRenew === 0} onClick={confirm}>
              {busy ? 'Working…' : toRenew > 0 ? `Mark ${toRenew} as verified` : `Mark ${toExpire} as expired`}
            </button>
            <button type="button" className="bk-btn" disabled={busy} onClick={reset}>Cancel</button>
          </div>
        </div>
      )}

      {pending.map(rep => {
        const count = rep.rows.filter(r => r.outcome === 'expire').length;
        return (
          <div key={rep.id} role="region" aria-label="Report waiting for you to confirm" className="bgl-waiting">
            <p className="bgl-waiting__title">
              Waiting for you to confirm: report emailed {fmtDate(rep.receivedAt)}{rep.sender ? ` by ${rep.sender}` : ''}
            </p>
            <p className="bk-small bgl-waiting__text">
              Nobody has been changed yet. Anyone renewed in the meantime is checked again and left alone.
            </p>
            <Outcomes rows={rep.rows} preview />
            <div className="bk-actions">
              <button type="button" className="bk-btn bk-btn--primary" disabled={!!deciding} onClick={() => decide(rep.id, 'confirm')}>
                {deciding === rep.id ? 'Working…' : `Mark ${count} as expired`}
              </button>
              <button type="button" className="bk-btn" disabled={!!deciding} onClick={() => decide(rep.id, 'dismiss')}>
                Dismiss
              </button>
            </div>
          </div>
        );
      })}

      {reports.length > 0 && (
        <div className="bgl-recent">
          <h3 className="bgl-recent__title">Recent reports</h3>
          {reports.map(rep => (
            <div key={rep.id} className="bgl-recent__item">
              <button
                type="button"
                className="bgl-toggle"
                aria-expanded={open === rep.id}
                onClick={() => setOpen(open === rep.id ? null : rep.id)}
              >
                <span>{fmtDate(rep.createdAt)} · {rep.source === 'email' ? `emailed${rep.sender ? ` by ${rep.sender}` : ''}` : 'uploaded'} · {reportSummary(rep)}</span>
                <Chevron />
              </button>
              {open === rep.id && (
                <div className="bgl-recent__body">
                  <Outcomes
                    rows={rep.rows}
                    preview={rep.status === 'PENDING'}
                    dismissed={rep.status === 'DISMISSED'}
                    onUndo={['APPLIED', 'APPLYING'].includes(rep.status) ? (gymnastId) => undo(rep.id, gymnastId) : null}
                    undoing={undoing}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
